import React from 'react';
import { CONTACT_INFO } from '../constants/companyInfo';
import { getStaticContent } from '../constants/staticContent';
import Logo from '../assets/logo.png';
import { MdFavorite, MdEmail, MdPhone } from 'react-icons/md';
import { speak, SLOGAN } from '../utils/speak';

const Footer = ({ language = 'en', onNavigate }) => {
  const { FOOTER_CONTENT } = getStaticContent(language);
  const year = new Date().getFullYear();

  const handleNavClick = (section) => {
    if (onNavigate) {
      onNavigate(section);
    }
  };

  return (
    <footer
      id="footer"
      className="relative overflow-hidden text-white"
      style={{
        background: 'linear-gradient(135deg, #1a3a1a 0%, #2d5a27 40%, #1f4a2e 70%, #3d2b0a 100%)',
      }}
    >
      {/* Background Effects — soil glow + wheat bloom + leaf arc */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Wheat gold bloom */}
        <div
          className="absolute rounded-full"
          style={{
            top: '-60px',
            right: '-40px',
            width: '380px',
            height: '380px',
            borderRadius: '0 0 0 100%',
            background: 'rgba(212,168,83,0.07)',
            filter: 'blur(60px)',
          }}
        />
        {/* Soil brown glow */}
        <div
          className="absolute rounded-full"
          style={{
            bottom: '-60px',
            left: '-40px',
            width: '340px',
            height: '340px',
            background: 'rgba(180,100,30,0.12)',
            filter: 'blur(80px)',
          }}
        />
        {/* Leaf green shimmer */}
        <div
          className="absolute rounded-full"
          style={{
            top: '40px',
            left: '45%',
            width: '200px',
            height: '200px',
            background: 'rgba(139,195,74,0.06)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">

        {/* Top Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">

          {/* Brand */}
          <div>
            <button
              type="button"
              onClick={() => {
                speak(SLOGAN.text, SLOGAN.lang);
                handleNavClick('home');
              }}
              aria-label="CropShield — go to home"
              className="flex items-center gap-4 mb-6 text-left hover:opacity-90 transition"
            >
              <div
                className="rounded-2xl shadow-lg p-2"
                style={{ background: 'rgba(255,255,255,0.95)' }}
              >
                <img
                  src={Logo}
                  alt="CropShield Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                  CropShield
                </h3>

              </div>
            </button>

            <p
              className="text-sm md:text-base leading-relaxed max-w-sm"
              style={{ color: '#a8c89b' }}
            >
              {FOOTER_CONTENT.brandDescription}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="text-xs font-bold uppercase tracking-widest"
              style={{ color: '#d4a853' }}
            >
              {FOOTER_CONTENT.quickLinksTitle}
            </h4>

            {/* Wheat-gold → leaf-green gradient bar */}
            <div
              className="mt-3 mb-6 rounded-full"
              style={{
                width: '48px',
                height: '2px',
                background: 'linear-gradient(90deg, #d4a853, #8bc34a)',
              }}
            />

            <ul className="space-y-4">
              {['home', 'why', 'usage', 'downloads', 'about'].map((id, idx) => ({ id, label: FOOTER_CONTENT.quickLinks[idx] })).map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={() => handleNavClick(item.id)}
                    className="group inline-flex items-center transition-all duration-300 text-sm md:text-base"
                    style={{ color: '#c8dfc0' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#d4a853')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#c8dfc0')}
                  >
                    <span
                      className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 mr-2"
                      style={{ color: '#8bc34a' }}
                    >
                      →
                    </span>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Features */}
          <div>
            <h4
              className="text-xs font-bold uppercase tracking-widest"
              style={{ color: '#d4a853' }}
            >
              {FOOTER_CONTENT.featuresTitle}
            </h4>

            <div
              className="mt-3 mb-6 rounded-full"
              style={{
                width: '48px',
                height: '2px',
                background: 'linear-gradient(90deg, #d4a853, #8bc34a)',
              }}
            />

            <ul className="space-y-4 text-sm md:text-base">
              {FOOTER_CONTENT.featuresItems.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 transition-colors duration-300"
                  style={{ color: '#c8dfc0' }}
                >
                  {/* Leaf-green dot */}
                  <span
                    className="mt-2 flex-shrink-0 rounded-full"
                    style={{
                      width: '6px',
                      height: '6px',
                      background: '#8bc34a',
                      display: 'inline-block',
                    }}
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4
              className="text-xs font-bold uppercase tracking-widest"
              style={{ color: '#d4a853' }}
            >
              {FOOTER_CONTENT.contactTitle}
            </h4>

            <div
              className="mt-3 mb-6 rounded-full"
              style={{
                width: '48px',
                height: '2px',
                background: 'linear-gradient(90deg, #d4a853, #8bc34a)',
              }}
            />

            <ul className="space-y-5 text-sm md:text-base">
              <li>
                <a
                  href={`mailto:${CONTACT_INFO.EMAIL}`}
                  className="flex items-center gap-3 transition-all duration-300"
                  style={{ color: '#c8dfc0' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#c8dfc0')}
                >
                  {/* Icon badge */}
                  <span
                    className="flex items-center justify-center flex-shrink-0 rounded-lg w-8 h-8"
                    style={{
                      background: 'rgba(139,195,74,0.15)',
                      border: '0.5px solid rgba(139,195,74,0.3)',
                      color: '#8bc34a',
                    }}
                  >
                    <MdEmail size={15} />
                  </span>
                  <span>{CONTACT_INFO.EMAIL}</span>
                </a>
              </li>

              <li>
                <a
                  href={`tel:${CONTACT_INFO.PHONE.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 transition-all duration-300"
                  style={{ color: '#c8dfc0' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#c8dfc0')}
                >
                  <span
                    className="flex items-center justify-center flex-shrink-0 rounded-lg w-8 h-8"
                    style={{
                      background: 'rgba(139,195,74,0.15)',
                      border: '0.5px solid rgba(139,195,74,0.3)',
                      color: '#8bc34a',
                    }}
                  >
                    <MdPhone size={15} />
                  </span>
                  <span>{CONTACT_INFO.PHONE}</span>
                </a>
              </li>

              <li>
                <span
                  className="cursor-pointer transition-colors duration-300"
                  style={{ color: '#a8a090' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#d4a853')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#a8a090')}
                >
                  {FOOTER_CONTENT.privacy}
                </span>
              </li>

              <li>
                <span
                  className="cursor-pointer transition-colors duration-300"
                  style={{ color: '#a8a090' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#d4a853')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#a8a090')}
                >
                  {FOOTER_CONTENT.terms}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider — wheat-gold to leaf-green blend */}
        <div
          className="my-12 md:my-14"
          style={{
            height: '1px',
            background:
              'linear-gradient(90deg, transparent, rgba(212,168,83,0.35), rgba(139,195,74,0.35), transparent)',
          }}
        />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-5">
          <p className="text-sm md:text-base text-center md:text-left" style={{ color: '#7a9970' }}>
            © {year}{' '}
            <span className="font-semibold" style={{ color: '#d4a853' }}>
              CropShield
            </span>
            . {FOOTER_CONTENT.rights}
          </p>

          <div className="flex items-center gap-3">
            <p className="flex items-center gap-2 text-sm md:text-base" style={{ color: '#7a9970' }}>
              {FOOTER_CONTENT.madeWith}
              <MdFavorite className="text-red-400 animate-pulse" />
              {FOOTER_CONTENT.forFarmers}
            </p>

            {/* Kisan First badge */}
            <span
              className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1 font-bold uppercase tracking-widest"
              style={{
                background: 'rgba(139,195,74,0.12)',
                border: '0.5px solid rgba(139,195,74,0.3)',
                color: '#8bc34a',
              }}
            >
              🌱 {FOOTER_CONTENT.kisanFirst}
            </span>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;